import type { ReactNode } from 'react';
import {
  Android,
  Apple,
  Chart,
  Eye,
  Monitor,
  MouseCircle,
  People,
  Profile2User,
} from 'iconsax-react';

import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
} from '@src/design-system/dialog';
import CloseSquare from '@shared/assets/icons/shared/close-square';
import { cn } from '@src/design-system/helpers';
import { Skeleton } from '@src/design-system/skeleton';
import { formatDate } from '@src/shared/utils/helpers/format-date';
import type {
  TAnnouncement,
  TAnnouncementPlatformBreakdownItem,
  TAnnouncementPlatformKey,
} from '@src/shared/types/announcement';
import { AnnouncementStatusBadge } from '../announcement-status-badge';
import announcementsApi from '../../services/announcements-api';
import memberApi from '@shared/services/member-api';
import {
  formatCompactCount,
  formatCtrDisplay,
} from '../../utils/format-announcement-stats';
import { isAnnouncementVideoMime } from '../../utils/upload-announcement-media';

type AnnouncementStatisticsDialogProps = {
  announcement: TAnnouncement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function memberDisplayName(
  member: { first_name?: string; last_name?: string } | null | undefined
): string {
  if (!member) return '';
  return [member.first_name, member.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
}

function SectionTitle({ children }: { children: string }) {
  return (
    <p className='text-sm! font-medium! uppercase text-gray-600'>{children}</p>
  );
}

function MetricCard({
  title,
  value,
  icon,
  valueClassName,
  isLoading,
}: {
  title: string;
  value: string;
  icon: ReactNode;
  valueClassName?: string;
  isLoading?: boolean;
}) {
  return (
    <div className='flex h-[100px] max-h-[100px] flex-col justify-between overflow-hidden rounded-2xl border border-pale-border bg-white px-4 py-3 shadow-sm'>
      <div className='flex items-start justify-between gap-2'>
        <p className='text-sm font-medium text-[#0F132499]'>{title}</p>
        <div className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-50'>
          {icon}
        </div>
      </div>
      {isLoading ? (
        <Skeleton className='h-7 w-16' />
      ) : (
        <p
          className={cn('text-2xl font-semibold leading-none', valueClassName)}
        >
          {value}
        </p>
      )}
    </div>
  );
}

function AudienceBreakdownRow({
  label,
  percentage,
}: {
  label: string;
  percentage: number;
}) {
  return (
    <div className='flex items-center gap-3'>
      <p
        className='w-[117px] shrink-0 truncate text-sm font-medium text-gray-800'
        title={label}
      >
        {label}
      </p>
      <div className='h-3 flex-1 overflow-hidden rounded-full bg-primary-100'>
        <div
          className='h-full rounded-full bg-primary-1000'
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className='shrink-0 text-sm font-medium text-gray-800'>
        {percentage}%
      </p>
    </div>
  );
}

const DEVICE_CARDS = [
  { key: 'WEB', label: 'Web', Icon: Monitor },
  { key: 'ANDROID', label: 'Android', Icon: Android },
  { key: 'IOS', label: 'iOS', Icon: Apple },
] as const satisfies readonly {
  key: TAnnouncementPlatformKey;
  label: string;
  Icon: typeof Monitor;
}[];

function platformPercentage(
  platforms: TAnnouncementPlatformBreakdownItem[] | undefined,
  key: TAnnouncementPlatformKey
): number {
  return platforms?.find((platform) => platform.key === key)?.percentage ?? 0;
}

const DEVICE_CARD_STYLES: Record<
  TAnnouncementPlatformKey,
  { card: string; value: string; label: string; icon: string }
> = {
  WEB: {
    card: 'border-secondary-200 bg-secondary-100',
    value: 'text-secondary-1000',
    label: 'text-secondary-800',
    icon: 'text-secondary-1000',
  },
  ANDROID: {
    card: 'border-primary-200 bg-primary-100',
    value: 'text-primary-1000',
    label: 'text-gray-600',
    icon: 'text-primary-1000',
  },
  IOS: {
    card: 'border-green-200 bg-green-50',
    value: 'text-green-500',
    label: 'text-green-400',
    icon: 'text-green-500',
  },
};

function DeviceCard({
  deviceKey,
  label,
  percentage,
}: {
  deviceKey: (typeof DEVICE_CARDS)[number]['key'];
  label: string;
  percentage: number;
}) {
  const styles = DEVICE_CARD_STYLES[deviceKey];
  const Icon =
    DEVICE_CARDS.find((device) => device.key === deviceKey)?.Icon ?? Monitor;

  return (
    <div
      className={cn(
        'flex h-[145px] max-h-[145px] flex-col items-center justify-center gap-3 rounded-2xl border px-4 py-3 shadow-sm',
        styles.card
      )}
    >
      <Icon
        size={28}
        variant='Outline'
        className={styles.icon}
      />
      <div className='flex flex-col items-center gap-1 text-center'>
        <p className={cn('text-2xl font-semibold leading-none', styles.value)}>
          {percentage}%
        </p>
        <p className={cn('text-sm font-medium', styles.label)}>{label}</p>
      </div>
    </div>
  );
}

export function AnnouncementStatisticsDialog({
  announcement,
  open,
  onOpenChange,
}: AnnouncementStatisticsDialogProps) {
  const { data: stats, isLoading } = announcementsApi.useGetDetailStats(
    announcement.id,
    { enabled: open }
  );
  const { data: createdBy } = memberApi.useFetchOne(
    { id: announcement.created_by_member },
    { enabled: open && Boolean(announcement.created_by_member) }
  );

  const mediaUrl = announcement.media?.file;
  const isVideo = announcement.media?.mime_type
    ? isAnnouncementVideoMime(announcement.media.mime_type)
    : false;
  const createdByName = memberDisplayName(createdBy);
  const totalViews = stats?.total_views ?? announcement.views_count ?? 0;
  const ctaClicks = stats?.cta_clicks ?? announcement.clicks_count ?? 0;
  const audienceBreakdown = stats?.programs?.length
    ? stats.programs
    : (announcement.programs ?? []).map((program) => ({
        key: String(program.id),
        label: program.name,
        count: 0,
        percentage: 0,
      }));

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className='flex max-h-[90vh] w-[95vw] max-w-[600px] flex-col overflow-hidden p-0'>
        <div className='border-b border-primary-100 px-4 py-4'>
          <div className='flex items-start gap-2'>
            <div className='size-16 shrink-0 overflow-hidden rounded-xl bg-gray-200'>
              {mediaUrl ? (
                isVideo ? (
                  <video
                    src={mediaUrl}
                    className='size-full object-cover'
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={mediaUrl}
                    alt={announcement.name}
                    className='size-full object-cover'
                  />
                )
              ) : null}
            </div>

            <div className='min-w-0 flex-1'>
              <p className='truncate text-base! font-medium capitalize text-gray-900'>
                {announcement.name}
              </p>
              <div className='mt-1 flex flex-wrap items-center gap-2'>
                <AnnouncementStatusBadge announcement={announcement} />
                {createdByName ? (
                  <>
                    <span className='text-sm text-gray-600'>
                      By: {createdByName}
                    </span>
                    <span className='text-sm text-gray-600'>|</span>
                  </>
                ) : null}
                {announcement.created_at ? (
                  <span className='text-sm text-gray-600'>
                    Created: {formatDate(announcement.created_at)}
                  </span>
                ) : null}
              </div>
            </div>

            <DialogClose className='shrink-0 rounded-md p-1 text-gray-800 hover:bg-primary-30'>
              <CloseSquare />
              <span className='sr-only'>Close</span>
            </DialogClose>
          </div>
        </div>

        <DialogBody className='space-y-5 overflow-y-auto p-4'>
          <section className='space-y-3'>
            <SectionTitle>Reach</SectionTitle>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <MetricCard
                title='Total Views'
                value={formatCompactCount(totalViews)}
                valueClassName='text-secondary-1000'
                isLoading={isLoading}
                icon={
                  <Eye
                    size={20}
                    variant='Outline'
                    className='text-primary-1000'
                  />
                }
              />
              <MetricCard
                title='Students Reached'
                value={formatCompactCount(stats?.students_reached)}
                valueClassName='text-primary-1000'
                isLoading={isLoading}
                icon={
                  <Profile2User
                    size={20}
                    variant='Outline'
                    className='text-primary-1000'
                  />
                }
              />
              <MetricCard
                title='Audience Size'
                value={formatCompactCount(stats?.audience_size)}
                valueClassName='text-blue-500'
                isLoading={isLoading}
                icon={
                  <People
                    size={20}
                    variant='Outline'
                    className='text-primary-1000'
                  />
                }
              />
              <MetricCard
                title='Reach Rate'
                value={formatCtrDisplay(stats?.reach_rate)}
                valueClassName='text-yellow-500'
                isLoading={isLoading}
                icon={
                  <Chart
                    size={20}
                    variant='Linear'
                    className='text-primary-1000'
                  />
                }
              />
            </div>
          </section>

          <section className='space-y-3'>
            <SectionTitle>Engagement</SectionTitle>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <MetricCard
                title='CTA Clicks'
                value={formatCompactCount(ctaClicks)}
                valueClassName='text-green-500'
                isLoading={isLoading}
                icon={
                  <MouseCircle
                    size={20}
                    variant='Outline'
                    className='text-primary-1000'
                  />
                }
              />
              <MetricCard
                title='Avg CTR'
                value={formatCtrDisplay(stats?.avg_ctr)}
                valueClassName='text-yellow-500'
                isLoading={isLoading}
                icon={
                  <Chart
                    size={20}
                    variant='Linear'
                    className='text-primary-1000'
                  />
                }
              />
            </div>
          </section>

          {audienceBreakdown.length ? (
            <section className='space-y-3'>
              <SectionTitle>Audience breakdown</SectionTitle>
              <div className='space-y-3'>
                {audienceBreakdown.map((item) => (
                  <AudienceBreakdownRow
                    key={item.key || item.label}
                    label={item.label}
                    percentage={item.percentage}
                  />
                ))}
              </div>
            </section>
          ) : null}

          <section className='space-y-3'>
            <SectionTitle>Device Breakdown</SectionTitle>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
              {DEVICE_CARDS.map((device) => (
                <DeviceCard
                  key={device.key}
                  deviceKey={device.key}
                  label={device.label}
                  percentage={
                    isLoading
                      ? 0
                      : platformPercentage(stats?.platform, device.key)
                  }
                />
              ))}
            </div>
          </section>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
